/**
 * Todo lo que este formulario sabe del servidor cabe aqui.
 *
 * Las rutas son RELATIVAS a proposito (`/api/...`, nunca `http://localhost:3001/api`).
 * En desarrollo las reenvia Vite; en produccion las sirve el mismo dominio. Con una URL
 * absoluta dentro del build, el dia del despliegue el formulario sigue llamando a la
 * laptop de quien lo compilo, y no da error: da un fallo de red en el navegador del
 * cliente que aqui nadie ve.
 */

export interface Zona {
  valor: string
  etiqueta: string
}

export interface DatosDelFormulario {
  nombre: string
  telefono: string
  correo: string
  zona: string
  consumoKwh: string
  latitud: number | null
  longitud: number | null
}

export type Respuesta =
  | { ok: true; solicitudId: string }
  | { ok: false; errores?: Record<string, string>; error?: string }

export interface OpcionesDelFormulario {
  zonas: Zona[]
  contacto: { whatsapp: string; email: string }
}

/**
 * Las zonas y el contacto los manda el servidor.
 *
 * Las zonas, porque cada una lleva asociada su irradiacion en el motor: escritas a mano
 * aqui, añadir una ciudad significa acordarse de tocar tres proyectos. El contacto,
 * porque el telefono de SunSmart ya vive en un `.env` y no puede tener una segunda
 * version compilada dentro de un bundle de JavaScript.
 */
export async function cargarFormulario(): Promise<OpcionesDelFormulario> {
  const respuesta = await fetch('/api/formulario')
  if (!respuesta.ok) throw new Error('No se pudo cargar el formulario')
  return (await respuesta.json()) as OpcionesDelFormulario
}

/**
 * Envia el formulario.
 *
 * Nunca lanza por un error del servidor: devuelve el fallo como dato para que el
 * formulario pueda pintarlo debajo del campo que toca. Una excepcion aqui acabaria en
 * una pantalla en blanco, que para el cliente es indistinguible de "esta pagina no
 * funciona" — y se va.
 */
export async function enviarSolicitud(
  datos: DatosDelFormulario,
  planilla: File | null,
): Promise<Respuesta> {
  const cuerpo = new FormData()
  cuerpo.set('nombre', datos.nombre.trim())
  // Se manda tal y como lo escribio: normalizarlo es del servidor, que es el que decide
  // que se guarda. Hacerlo tambien aqui serian dos reglas que un dia dejan de coincidir.
  cuerpo.set('telefono', datos.telefono.trim())
  cuerpo.set('correo', datos.correo.trim())
  cuerpo.set('zona', datos.zona)
  if (datos.consumoKwh.trim()) cuerpo.set('consumoKwh', datos.consumoKwh.trim())
  if (datos.latitud !== null) cuerpo.set('latitud', String(datos.latitud))
  if (datos.longitud !== null) cuerpo.set('longitud', String(datos.longitud))
  if (planilla) cuerpo.set('planilla', planilla)

  try {
    const respuesta = await fetch('/api/solicitudes', { method: 'POST', body: cuerpo })
    return (await respuesta.json()) as Respuesta
  } catch {
    return {
      ok: false,
      error: 'No pudimos conectar. Revisa tu conexion e intentalo de nuevo.',
    }
  }
}

export interface Recomendacion {
  potencia_kwp: number
  paneles: number
  potencia_panel_w: number
  inversores: number
  potencia_inversor_kw: number
  generacion_mensual_kwh: number
  cobertura_pct: number
  area_paneles_m2: number
  area_techo_m2: number
  fuera_de_catalogo: boolean
}

/**
 * El sistema que cubriria ese consumo, para enseñarlo mientras la persona escribe.
 *
 * Devuelve `null` en vez de fallar cuando no hay recomendacion —el servidor contesta
 * 204 si el motor no esta— porque esto es un extra: quien entra al formulario viene a
 * dejar sus datos, y un fallo aqui no puede impedirselo ni enseñarle un error.
 *
 * **Lo que llega NUNCA lleva precios.** El precio lo dice un asesor; esta pantalla solo
 * dice el tamaño.
 */
export async function pedirRecomendacion(
  consumoKwh: number,
  zona: string,
  senal: AbortSignal,
): Promise<Recomendacion | null> {
  try {
    const respuesta = await fetch(
      `/api/recomendacion?consumoKwh=${consumoKwh}&zona=${encodeURIComponent(zona)}`,
      { signal: senal },
    )
    if (!respuesta.ok || respuesta.status === 204) return null
    return (await respuesta.json()) as Recomendacion
  } catch {
    return null
  }
}

/**
 * Pide la ubicacion al navegador.
 *
 * Con las coordenadas exactas se consulta la irradiacion real del techo en NASA POWER,
 * en vez del valor de referencia de la ciudad. Es opcional y tiene que seguir siendolo:
 * un permiso de ubicacion obligatorio hace que una parte de la gente cierre la pestaña.
 */
export function pedirUbicacion(): Promise<{ latitud: number; longitud: number }> {
  return new Promise((resolver, rechazar) => {
    if (!('geolocation' in navigator)) {
      rechazar(new Error('Tu navegador no puede compartir la ubicacion'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (posicion) =>
        resolver({
          latitud: Number(posicion.coords.latitude.toFixed(5)),
          longitud: Number(posicion.coords.longitude.toFixed(5)),
        }),
      () => rechazar(new Error('No pudimos leer tu ubicacion')),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 600_000 },
    )
  })
}
